import {createAsyncThunk} from '@reduxjs/toolkit';
import {updateSettings, getOptimalConcurrency} from '../slices/settingsSlice';
import {readDesignV2} from '../../utils/designV2';
import {setInputConcurrencyLevel} from "../slices/appSlice";
import {refreshAndRetry} from '../utils/refreshAndRetry';

const VALID_THEMES = new Set(['dark', 'light', 'auto']);

export const loadSettings = createAsyncThunk(
    'settings/load',
    async (_, {dispatch}) => {
        try {
            const defaults = {
                enableRecursive: true,
                // NOTE: backupOriginalFiles lives in fixSettings (persisted via
                // settingsMiddleware), not here — the old copy in this slice was
                // vestigial (never read) and was removed to prevent drift.
                concurrencyLevel: getOptimalConcurrency(),
                logOnlyChanges: false,
                overwriteExistingFixedFiles: false,
                keyNotation: 'camelot',
            };
            const raw = localStorage.getItem('appSettings');
            const settings = raw ? JSON.parse(raw) : null;
            // The dedicated `mixgoat-theme` key is the local source of truth
            // until the server preference loads. `appSettings` is only
            // flushed on beforeunload, which mobile browsers ignore, so its
            // theme field can be stale — prefer the dedicated key.
            const savedTheme = localStorage.getItem('mixgoat-theme');
            const merged = {...defaults, ...settings};
            // No UI ever exposed concurrencyLevel, so a persisted value is only
            // the old hard-coded 3 (or a previous machine's number) leaking
            // through appSettings — always take this machine's computed value.
            merged.concurrencyLevel = defaults.concurrencyLevel;
            if (savedTheme && VALID_THEMES.has(savedTheme)) merged.theme = savedTheme;
            // Same rule for the design flag: localStorage 'mixgoat-design-v2'
            // (utils/designV2.js) is the only source of truth, so a persisted
            // appSettings copy can never flip the JSX branches without the CSS.
            merged.designV2 = readDesignV2();
            const savedDjName = localStorage.getItem('mixgoat-dj-name');
            if (savedDjName != null) merged.djName = savedDjName;
            dispatch(updateSettings(merged));
            dispatch(setInputConcurrencyLevel(merged.concurrencyLevel));
        } catch (error) {
            console.error("Error loading settings:", error);
        }
    }
);

export const saveSettings = createAsyncThunk(
    'settings/save',
    async (settings) => {
        try {
            localStorage.setItem('appSettings', JSON.stringify(settings));
        } catch (error) {
            console.error("Error saving settings:", error);
        }
    }
);

// Load preferences (currently just `theme`) from user-library-service. Runs
// after auth resolves. The server is the source of truth for logged-in
// users — anything different from localStorage is reconciled here.
//
// Auth note: on desktop, `accessToken` is a real bearer string. On web,
// it's always null — cookies handle auth via `credentials: include` in
// the shim. So gate on `isAuthenticated`, not on token presence.
export const loadPreferences = createAsyncThunk(
    'settings/loadPreferences',
    async (_, { dispatch, getState }) => {
        try {
            const state = getState();
            if (!state.auth?.isAuthenticated) return;
            if (!window.electron?.library?.getPreferences) return;
            // refreshAndRetry: a stale 15-min token refreshes + retries instead
            // of silently failing (it resolves the token itself per platform).
            const result = await refreshAndRetry(
                (token) => window.electron.library.getPreferences(token)
            );
            // Response envelope: { data: { theme, updatedAt } } from
            // user-library-service. The desktop axios wrapper returns that
            // shape verbatim; the web shim returns the same parsed JSON.
            const payload = result?.data || result;
            const serverTheme = payload?.theme;
            if (!serverTheme || !VALID_THEMES.has(serverTheme)) return;
            const currentTheme = getState().settings.theme;
            if (serverTheme === currentTheme) return;
            dispatch(updateSettings({ theme: serverTheme }));
        } catch (error) {
            console.error('Error loading preferences:', error);
        }
    }
);

// Write a preference change through to the server. Fire-and-forget — the
// local change has already been applied to Redux + localStorage so the UI
// doesn't wait. A server failure logs but doesn't surface; next load
// reconciles.
export const savePreferences = createAsyncThunk(
    'settings/savePreferences',
    async (patch, { getState }) => {
        try {
            const state = getState();
            if (!state.auth?.isAuthenticated) return; // Anonymous — localStorage only
            if (!window.electron?.library?.updatePreferences) return;
            await refreshAndRetry(
                (token) => window.electron.library.updatePreferences(token, patch)
            );
        } catch (error) {
            console.error('Error saving preferences:', error);
        }
    }
);
